/**
 * Toolbar + dialogs for column pickers and per-column text filters, wired by {@link useConfiguredTable}.
 * Uses styled divs (not MUI Box) for layout only; interactive controls go through shared form/button components.
 */
import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import AppButton from "../Button/Button";
import AppDialog from "../Dailog/Dailog";
import AppCheckbox from "../FormInputs/AppCheckbox";
import FormInput from "../FormInputs/MuiTextField";
import { LabelText } from "../Typography/AppTypography";

const ControlsToolbar = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));

const DialogBody = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const ColumnsActionRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  flexWrap: "wrap",
}));

const ColumnsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr",
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
}));

// Theme-aware tile styling driven by data attributes so we avoid invalid DOM props (e.g. passing `checked` to a div).
const ColumnOptionTile = styled("div")(({ theme }) => ({
  border: "1px solid",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  borderColor: theme.palette.divider,
  backgroundColor: "transparent",
  opacity: 1,
  '&[data-checked="true"]': {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(255,152,0,0.06)",
  },
  '&[data-locked="true"]': {
    opacity: 0.8,
  },
}));

const TableControls = ({
  allColumns = [],
  visibleColumnKeys = [],
  onToggleColumn,
  onResetVisibleColumns,
  onSelectAllColumns,
  onDeselectAllColumns,
  filterableColumns = [],
  filters = {},
  onFilterChange,
  onClearFilters,
  showFiltersButton = true,
  showColumnsButton = true,
}) => {
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  return (
    <React.Fragment>
      <ControlsToolbar>
        {showFiltersButton && (
          <AppButton variant="primary" size="small" onClick={() => setFilterDialogOpen(true)}>
            Filters
          </AppButton>
        )}
        {showColumnsButton && (
          <AppButton variant="primary" size="small" onClick={() => setColumnDialogOpen(true)}>
            Columns
          </AppButton>
        )}
      </ControlsToolbar>

      <AppDialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
        title="Select Columns"
        showCloseButton
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
        contentSx={{
          maxHeight: "62vh",
          overflowY: "auto",
        }}
        actions={[
          { label: "Reset Default", variant: "ghost", onClick: onResetVisibleColumns },
          { label: "Done", variant: "primary", onClick: () => setColumnDialogOpen(false) },
        ]}
      >
        <DialogBody>
          <ColumnsActionRow>
            <AppButton variant="ghost" size="small" onClick={onSelectAllColumns}>
              Select All
            </AppButton>
            <AppButton variant="ghost" size="small" onClick={onDeselectAllColumns}>
              Deselect All
            </AppButton>
          </ColumnsActionRow>
          <ColumnsGrid>
            {allColumns.map((column) => {
              const checked = visibleColumnKeys.includes(column.key);
              return (
                <ColumnOptionTile
                  key={column.key}
                  data-checked={checked ? "true" : "false"}
                  data-locked={column.locked ? "true" : "false"}
                >
                  <AppCheckbox
                    checked={checked}
                    disabled={column.locked}
                    onChange={() => onToggleColumn(column.key)}
                    size="small"
                    label={`${column.label}${column.locked ? " (Always Visible)" : ""}`}
                    sx={{ m: 0, width: "100%" }}
                  />
                </ColumnOptionTile>
              );
            })}
          </ColumnsGrid>
        </DialogBody>
      </AppDialog>

      <AppDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        title="Column Filters"
        showCloseButton
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
        contentSx={{
          maxHeight: "62vh",
          overflowY: "auto",
        }}
        actions={[
          { label: "Clear", variant: "ghost", onClick: onClearFilters },
          { label: "Apply", variant: "primary", onClick: () => setFilterDialogOpen(false) },
        ]}
      >
        <DialogBody>
          {filterableColumns.length === 0 && (
            <LabelText>No configured filters available.</LabelText>
          )}
          {filterableColumns.map((column) => (
            <FormInput
              key={column.key}
              type="text"
              name={column.key}
              label={column.label}
              value={filters[column.key] || ""}
              onChange={(event) => onFilterChange(column.key, event.target.value)}
              fullWidth
              size="small"
              margin="dense"
            />
          ))}
        </DialogBody>
      </AppDialog>
    </React.Fragment>
  );
};

export default TableControls;
